import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { formatDateInputInTz, DEFAULT_TIMEZONE } from '@/utils/timezone';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import {
  Invoice,
  InvoiceWithRelations,
  InvoiceFilters,
  InvoiceStats,
  CreateInvoiceInput,
  UpdateInvoiceInput,
  InvoiceLineItem,
  INVOICE_SELECT,
} from '@/types/invoice';
import { useToast } from '@/hooks/use-toast';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { useVisibility } from '@/hooks/useVisibility';

// Fetch all invoices with optional filters
export const useInvoices = (filters?: InvoiceFilters) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['invoices', filters],
    queryFn: async () => {
      let query = supabase
        .from('invoices')
        .select(INVOICE_SELECT)
        .order('created_at', { ascending: false });

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.customerId) {
        query = query.eq('customer_id', filters.customerId);
      }

      if (filters?.dateFrom) {
        query = query.gte('issue_date', formatDateInputInTz(filters.dateFrom, filters.timezone ?? DEFAULT_TIMEZONE));
      }

      if (filters?.dateTo) {
        query = query.lte('issue_date', formatDateInputInTz(filters.dateTo, filters.timezone ?? DEFAULT_TIMEZONE));
      }

      if (filters?.search) {
        query = query.or(`invoice_number.ilike.%${filters.search}%,customer_name.ilike.%${filters.search}%,customer_phone.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || []).map(invoice => ({
        ...invoice,
        line_items: Array.isArray(invoice.line_items)
          ? (invoice.line_items as unknown as InvoiceLineItem[])
          : JSON.parse(invoice.line_items as string || '[]') as InvoiceLineItem[]
      })) as InvoiceWithRelations[];
    },
    enabled: !!user,
  });
};

// Fetch single invoice
export const useInvoice = (id: string | null) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from('invoices')
        .select(INVOICE_SELECT)
        .eq('id', id)
        .single();

      if (error) throw error;

      return {
        ...data,
        line_items: Array.isArray(data.line_items)
          ? (data.line_items as unknown as InvoiceLineItem[])
          : JSON.parse(data.line_items as string || '[]') as InvoiceLineItem[]
      } as InvoiceWithRelations;
    },
    enabled: !!user && !!id,
  });
};

// Fetch invoice stats for dashboard
export const useInvoiceStats = (dateFrom?: Date, dateTo?: Date, timezone: string = DEFAULT_TIMEZONE) => {
  const { user } = useAuth();
  const { canSeeCompanyFinancials } = useVisibility();

  return useQuery({
    queryKey: ['invoice-stats', dateFrom?.toISOString(), dateTo?.toISOString(), timezone],
    queryFn: async () => {
      let query = supabase
        .from('invoices')
        .select('status, total_amount, amount_paid, amount_outstanding');

      if (dateFrom) {
        query = query.gte('issue_date', formatDateInputInTz(dateFrom, timezone));
      }

      if (dateTo) {
        query = query.lte('issue_date', formatDateInputInTz(dateTo, timezone));
      }

      const { data, error } = await query;

      if (error) throw error;

      const stats: InvoiceStats = {
        totalInvoiced: 0,
        totalPaid: 0,
        totalOutstanding: 0,
        totalOverdue: 0,
        invoiceCount: data?.length || 0,
        draftCount: 0,
        sentCount: 0,
        paidCount: 0,
        overdueCount: 0,
        refundedCount: 0,
      };

      (data || []).forEach((invoice) => {
        stats.totalInvoiced += Number(invoice.total_amount) || 0;
        stats.totalPaid += Number(invoice.amount_paid) || 0;
        stats.totalOutstanding += Number(invoice.amount_outstanding) || 0;

        switch (invoice.status) {
          case 'draft':
            stats.draftCount++;
            break;
          case 'sent':
            stats.sentCount++;
            break;
          case 'paid':
            stats.paidCount++;
            break;
          case 'overdue':
            stats.overdueCount++;
            stats.totalOverdue += Number(invoice.amount_outstanding) || 0;
            break;
          case 'refunded':
            stats.refundedCount++;
            break;
        }
      });

      return stats;
    },
    enabled: !!user && canSeeCompanyFinancials,
  });
};

// Fetch invoices for a specific customer
export const useCustomerInvoices = (customerId: string | null) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['customer-invoices', customerId],
    queryFn: async () => {
      if (!customerId) return [];

      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          job:jobs(job_number)
        `)
        .eq('customer_id', customerId)
        .order('issue_date', { ascending: false });

      if (error) throw error;

      return (data || []).map(invoice => ({
        ...invoice,
        line_items: Array.isArray(invoice.line_items)
          ? (invoice.line_items as unknown as InvoiceLineItem[])
          : JSON.parse(invoice.line_items as string || '[]') as InvoiceLineItem[]
      })) as Invoice[];
    },
    enabled: !!user && !!customerId,
  });
};

// Create invoice mutation
export const useCreateInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const { tenantId: companyId } = useTenant();
  const tax = useCompanyTax();

  return useMutation({
    mutationFn: async (input: CreateInvoiceInput) => {
      if (!user || !companyId) throw new Error('User profile not found');

      const { data, error } = await supabase
        .from('invoices')
        .insert({
          company_id: companyId,
          created_by: user.id,
          invoice_number: '', // Will be auto-generated by trigger
          tax_rate_snapshot: tax.rate,
          tax_label_snapshot: tax.label,
          tax_inclusive_snapshot: tax.inclusive,
          ...input,
          line_items: input.line_items,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      queryClient.invalidateQueries({ queryKey: ['customer-invoices'] });
      toast({
        title: 'Invoice created',
        description: 'The invoice has been created successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error creating invoice',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Update invoice mutation
export const useUpdateInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateInvoiceInput) => {
      const updateData: Record<string, unknown> = { ...input };

      if (input.line_items) {
        updateData.line_items = input.line_items;
      }

      const { data, error } = await supabase
        .from('invoices')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', data.id] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      queryClient.invalidateQueries({ queryKey: ['customer-invoices'] });
      toast({
        title: 'Invoice updated',
        description: 'The invoice has been updated successfully.',
      });
    },
    onError: (error) => {
      // Two DB guards refuse money edits and prefix their message so we can name
      // the cause: invoices_xero_content_lock once Xero holds a payment, and
      // invoices_paid_total_lock when a paid/refunded invoice's total would change.
      const xeroLocked = /^XERO_LOCKED:\s*/i.test(error.message);
      const paidLocked = /^PAID_LOCKED:\s*/i.test(error.message);
      toast({
        title: xeroLocked ? 'Locked by Xero' : paidLocked ? 'Total is locked' : 'Error updating invoice',
        description: error.message.replace(/^(XERO|PAID)_LOCKED:\s*/i, ''),
        variant: 'destructive',
      });
    },
  });
};

// Mark invoice as sent
export const useMarkInvoiceSent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const { data, error } = await supabase
        .from('invoices')
        .update({
          status: 'sent',
          sent_at: new Date().toISOString(),
        })
        .eq('id', invoiceId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', data.id] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      toast({
        title: 'Invoice marked as sent',
        description: 'The invoice status has been updated.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating invoice',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Mark invoice as paid.
// Goes through the mark_invoice_paid RPC so the outstanding balance is recorded
// as a job payment (linked to the invoice) in the same transaction — the ledger,
// the job's payment_status and the invoice all agree, and the accounting sync
// has a payment to post.
export type MarkInvoicePaidMethod = 'cash' | 'card' | 'bank_transfer' | 'other';

export const useMarkInvoicePaid = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { timezone } = useTimezoneUtils();

  return useMutation({
    mutationFn: async ({
      invoiceId,
      paidDate,
      paymentMethod = 'other',
      notes,
    }: {
      invoiceId: string;
      paidDate?: string;
      paymentMethod?: MarkInvoicePaidMethod;
      notes?: string;
    }) => {
      const { data, error } = await supabase.rpc('mark_invoice_paid', {
        p_invoice_id: invoiceId,
        p_payment_method: paymentMethod,
        p_payment_date: paidDate || formatDateInputInTz(new Date(), timezone),
        p_notes: notes?.trim() ? notes.trim() : undefined,
      });

      if (error) throw error;
      const result = (data ?? {}) as { payment_id?: string | null; amount?: number; has_job?: boolean };
      return { id: invoiceId, ...result };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', data.id] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      queryClient.invalidateQueries({ queryKey: ['customer-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['job-payments'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job'] });
      toast({
        title: 'Invoice marked as paid',
        description: data.payment_id
          ? 'The outstanding balance was recorded as a payment on the job.'
          : 'The invoice has been marked as fully paid.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating invoice',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// ---------------------------------------------------------------------------
// Online payments (Square)
// ---------------------------------------------------------------------------

export type SendInvoiceDelivery = 'email' | 'link_only';

export interface SendInvoiceForPaymentResult {
  ok: true;
  payment_url: string | null;
  provider_invoice_id: string;
  already_published: boolean;
  email: { sent: boolean; reason?: string };
}

interface SendInvoiceFailure {
  ok: false;
  reason: string;
  message?: string;
}

const SEND_FAILURE_MESSAGES: Record<string, string> = {
  square_not_connected: 'Connect Square in Settings → Integrations to send invoices for online payment.',
  square_disconnected: 'Your Square connection needs attention. Reconnect it in Settings → Integrations.',
  no_location: 'Choose a Square location in Settings → Integrations first.',
  invoice_not_sendable: 'Only draft, sent or overdue invoices can be sent for payment.',
  currency_mismatch: 'This invoice currency does not match your Square location.',
};

// Publish the invoice to Square and (optionally) have Square email the
// customer a pay link. The edge function returns HTTP 200 with {ok:false}
// for expected failures so we map those to friendly messages here.
export const useSendInvoiceForPayment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ invoiceId, deliver }: { invoiceId: string; deliver: SendInvoiceDelivery }) => {
      const { data, error } = await supabase.functions.invoke('square-invoice-send', {
        body: { invoice_id: invoiceId, deliver },
      });
      if (error) throw new Error(error.message ?? 'Could not send invoice');

      const result = data as SendInvoiceForPaymentResult | SendInvoiceFailure | null;
      if (!result || result.ok === false) {
        const failure = result as SendInvoiceFailure | null;
        throw new Error(
          (failure && SEND_FAILURE_MESSAGES[failure.reason]) ||
            failure?.message ||
            'Could not send invoice',
        );
      }
      return { ...result, invoiceId, deliver };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', result.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      queryClient.invalidateQueries({ queryKey: ['customer-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['job-payments'] });

      const emailFailed = result.deliver === 'email' && result.email?.sent === false;
      if (emailFailed) {
        toast({
          title: 'Payment link ready, but the email was not sent',
          description: result.email?.reason || 'Copy the link and send it to the customer yourself.',
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: result.deliver === 'email' ? 'Invoice sent for payment' : 'Payment link ready',
        description:
          result.deliver === 'email'
            ? 'The customer has been emailed a link to pay online.'
            : 'Copy the link or print the invoice to share it.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Could not send invoice',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// ---------------------------------------------------------------------------
// Email the invoice itself (any status except cancelled)
// ---------------------------------------------------------------------------

export interface EmailInvoiceResult {
  ok: true;
  to: string;
  /** paid = sent as a receipt; due = sent with the balance and how to pay. */
  state: 'paid' | 'refunded' | 'due';
  balance_due: number;
  email_message_id: string;
}

const EMAIL_FAILURE_MESSAGES: Record<string, string> = {
  invoice_not_found: 'This invoice could not be found. Refresh and try again.',
  invoice_cancelled: 'A cancelled invoice cannot be emailed.',
  no_customer_email: 'Add an email address before sending.',
  invalid_email: 'That email address does not look right.',
  suppressed: 'This address bounced or unsubscribed before, so email to it is blocked.',
  recipient_inactive: 'This address is marked inactive by the email provider (it bounced before).',
  quota_exceeded: 'Your monthly email allowance is used up.',
  sending_paused: 'Email sending is paused for your account. Contact Steddy support.',
};

/** Pull the JSON error out of a non-2xx edge function response. */
async function functionErrorMessage(error: { message?: string; context?: unknown }): Promise<string> {
  const ctx = error.context as Response | undefined;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = await ctx.clone().json();
      if (body?.error) return String(body.error);
    } catch {
      /* fall through */
    }
  }
  return error.message ?? 'Could not email the invoice';
}

/**
 * Email an itemised invoice to the customer through send-invoice-email.
 * Paid invoices go out as a receipt, unpaid ones with the balance and how to
 * pay (including the Square pay link when one exists). `to` overrides the
 * invoice's email for this send only.
 */
export const useEmailInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ invoiceId, to, message }: { invoiceId: string; to?: string; message?: string }) => {
      const { data, error } = await supabase.functions.invoke('send-invoice-email', {
        body: { invoice_id: invoiceId, to: to?.trim() || undefined, message: message?.trim() || undefined },
      });
      if (error) throw new Error(await functionErrorMessage(error as { message?: string; context?: unknown }));

      const result = data as EmailInvoiceResult | SendInvoiceFailure | null;
      if (!result || result.ok === false) {
        const failure = result as SendInvoiceFailure | null;
        throw new Error(
          (failure && EMAIL_FAILURE_MESSAGES[failure.reason]) || failure?.message || 'Could not email the invoice',
        );
      }
      return { ...result, invoiceId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', result.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      queryClient.invalidateQueries({ queryKey: ['customer-invoices'] });
      toast({
        title: result.state === 'due' ? 'Invoice emailed' : 'Receipt emailed',
        description: `Sent to ${result.to}.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Invoice not emailed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Cancel invoice
export const useCancelInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      // If the invoice was published to Square, cancel it there first so the
      // customer's pay link stops working. A paid Square invoice cannot be
      // cancelled; refunds are handled from Square itself.
      const { data: existing, error: fetchError } = await supabase
        .from('invoices')
        .select('provider_invoice_id')
        .eq('id', invoiceId)
        .single();
      if (fetchError) throw fetchError;

      if (existing?.provider_invoice_id) {
        const { data: cancelData, error: cancelError } = await supabase.functions.invoke(
          'square-invoice-cancel',
          { body: { invoice_id: invoiceId } },
        );
        if (cancelError) throw new Error(cancelError.message ?? 'Could not cancel the Square invoice');
        const result = cancelData as { ok: boolean; reason?: string; message?: string } | null;
        if (result && result.ok === false && result.reason === 'already_paid') {
          throw new Error('This invoice has already been paid through Square and cannot be cancelled.');
        }
        if (result && result.ok === false && result.reason === 'square_error') {
          // Never cancel locally while the hosted pay page may still take money.
          throw new Error(result.message ? `Square could not cancel this invoice: ${result.message}` : 'Square could not cancel this invoice. Try again.');
        }
        if (result && result.ok) {
          // square-invoice-cancel already reconciled and cancelled our row.
          const { data: synced, error: syncErr } = await supabase.from('invoices').select().eq('id', invoiceId).single();
          if (syncErr) throw syncErr;
          return synced;
        }
        // not_published: nothing at Square; fall through to the local cancel.
      }

      const { data, error } = await supabase
        .from('invoices')
        .update({ status: 'cancelled' })
        .eq('id', invoiceId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', data.id] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      queryClient.invalidateQueries({ queryKey: ['customer-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['job-invoice'] });
      toast({
        title: 'Invoice cancelled',
        description: 'The invoice has been cancelled.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error cancelling invoice',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Delete invoice (any status; the UI decides who may delete a paid one)
export const useDeleteInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', invoiceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
      queryClient.invalidateQueries({ queryKey: ['customer-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['job-invoice'] });
      toast({
        title: 'Invoice deleted',
        description: 'The invoice has been deleted.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error deleting invoice',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Check if job already has an invoice
export const useJobInvoice = (jobId: string | null) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['job-invoice', jobId],
    queryFn: async () => {
      if (!jobId) return null;

      const { data, error } = await supabase
        .from('invoices')
        .select('id, invoice_number, status, total_amount')
        .eq('job_id', jobId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user && !!jobId,
  });
};
